import { useWorkspaceAsset } from '@gripp/shared-logic';
import type { ColumnData, SlideLayoutLocationState } from '@gripp/shared-ui';
import {
  SlideAction,
  SlideLayout,
  useSlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import { useMemo, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { AssetRouteProvider } from '../assetRouteContext';
import { AddAssetMediaType } from './addFilesLinksButton';
import { AssetCard } from './assetCard';
import { AssetFileCreateForm, CategoryEditTarget } from './assetFileCreateForm';
import { AssetFilters } from './assetFilters';
import EditCategory from './editCategory';

type AssetFileCreateFormPanelProps = {
  type: AddAssetMediaType;
  assetId: string;
  allFileIds: string[];
  categoryEdit: CategoryEditTarget | null;
  onCategoriesChange: (target: CategoryEditTarget) => void;
  onCategoryEdit: (target: CategoryEditTarget) => void;
  onCategoryEditClose: () => void;
  onComplete: () => void;
};

const AssetFileCreateFormPanel = ({
  type,
  assetId,
  allFileIds,
  categoryEdit,
  onCategoriesChange,
  onCategoryEdit,
  onCategoryEditClose,
  onComplete,
}: AssetFileCreateFormPanelProps) => {
  const { prepareSlideTransition } = useSlideLayout();

  return (
    <AssetFileCreateForm
      type={type}
      assetId={assetId}
      allFileIds={allFileIds}
      onComplete={() => {
        prepareSlideTransition(SlideAction.PREV);
        onComplete();
      }}
      onCategoryEdit={onCategoryEdit}
      categoryEdit={categoryEdit}
      onCategoriesChange={onCategoriesChange}
      onCategoryEditClose={onCategoryEditClose}
    />
  );
};

/**
 * SlideLayout panel for adding a file or link to an asset. Mirrors
 * `AssetFileEdit` — the panelized replacement for the old `CreateFileForm`
 * Modal. The `?type=file|link` query param selects which form to render.
 */
export const AssetFileCreate = () => {
  const { assetId = '', issueId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const type: AddAssetMediaType =
    searchParams.get('type') === 'link' ? 'link' : 'file';

  const locationState = location.state as SlideLayoutLocationState | null;
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;

  const { workspaceAsset } = useWorkspaceAsset(assetId);
  const allFileIds = useMemo(
    () => (workspaceAsset?.asset?.media ?? []).map((m) => m.id),
    [workspaceAsset?.asset?.media]
  );

  // The file whose categories the third column is editing; null when closed.
  const [categoryEdit, setCategoryEdit] = useState<CategoryEditTarget | null>(
    null
  );

  if (!assetId) return null;

  const basePath = issueId
    ? `/activities/${issueId}/asset/${assetId}`
    : `/assets/${assetId}`;

  const backToFiles = () =>
    navigate(`${basePath}/files/all`, {
      state: { slideLayoutInitialTransition: SlideAction.PREV },
    });

  const leftColumn: ColumnData = {
    title: 'assets.menu.title',
    mainComponent: (
      <AssetRouteProvider
        value={{
          basePath,
          listPath: issueId ? '/activities' : '/assets',
          isWorkspaceMode: false,
          issueId,
        }}
      >
        <AssetCard assetId={assetId} />
      </AssetRouteProvider>
    ),
    headerComponent: <AssetFilters assetId={assetId} />,
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    backButtonTransitionExitColumn: 'both',
    onBackButtonPress: backToFiles,
    disableScroll: false,
  };

  const rightColumn: ColumnData = {
    title: type === 'link' ? 'file.create.linkLabel' : 'file.create.label',
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    onBackButtonPress: backToFiles,
    disableScroll: true,
    mainComponent: (
      <AssetFileCreateFormPanel
        type={type}
        assetId={assetId}
        allFileIds={allFileIds}
        onComplete={backToFiles}
        onCategoryEdit={(target) =>
          setCategoryEdit((prev) =>
            prev?.fileKey === target.fileKey ? null : target
          )
        }
        categoryEdit={categoryEdit}
        onCategoriesChange={(target) =>
          setCategoryEdit((prev) =>
            prev?.fileKey === target.fileKey ? target : prev
          )
        }
        onCategoryEditClose={() => setCategoryEdit(null)}
      />
    ),
  };

  const thirdColumn: ColumnData | undefined = categoryEdit
    ? {
        title: 'file.create.category',
        showBackButton: true,
        disableScroll: true,
        onBackButtonPress: () => setCategoryEdit(null),
        mainComponent: (
          <EditCategory
            selectedCategories={categoryEdit.categories}
            onCategoriesChange={(categories) =>
              setCategoryEdit((prev) => prev && { ...prev, categories })
            }
          />
        ),
      }
    : undefined;

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      thirdColumn={thirdColumn}
      viewMode={ViewMode.DUAL}
      initialTransition={initialTransition}
    />
  );
};
